// disabling for entire file because it is being used at multiple places
/* eslint-disable no-undef */
import CryptoJS from 'crypto-js';
import { toInteger } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '../AppContext';
import { LIMIT } from '../common/constants';

const SALT = 'elefant-v1';

const getKey = () =>
  CryptoJS.SHA256(`${SALT}-${window.location.origin}`).toString();

const getTableStateFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const encrypted = params?.get('s');
  const tableKey = params?.get('t');

  if (!encrypted || !tableKey || tableKey === 'undefined') return null;

  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, getKey());
    const json = bytes.toString(CryptoJS.enc.Utf8);
    if (!json) return null;
    return {
      tableKey,
      state: JSON.parse(json),
    };
  } catch {
    return null;
  }
};

const useRetainedTableState = () => {
  const { state, dispatch } = useContext(AppContext);

  const [urlTrigger, setUrlTrigger] = useState(0);
  const [hydrated, setHydrated] = useState(false);
  const [hydratedTableKey, setHydratedTableKey] = useState(null);

  const [retainedSnapshot, setRetainedSnapshot] = useState(
    () => state?.retainedOrderFilters || {},
  );

  useEffect(() => {
    setRetainedSnapshot(state?.retainedOrderFilters || {});
  }, [urlTrigger]);

  useEffect(() => {
    const handleUrlChange = () => {
      setUrlTrigger((prev) => prev + 1);
      setHydrated(false);
      setHydratedTableKey(null);
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  useEffect(() => {
    const urlData = getTableStateFromUrl();

    if (!urlData) {
      setHydrated(true);
      setHydratedTableKey(null);
      return;
    }

    const { tableKey, state: urlTableState } = urlData;

    const mergedState = {
      ...retainedSnapshot,
      [tableKey]: urlTableState,
    };

    dispatch({
      type: 'SET_RETAINED_ORDER_FILTERS',
      data: mergedState,
    });

    setHydrated(true);
    setHydratedTableKey(tableKey);
  }, [urlTrigger, dispatch, retainedSnapshot]);

  const saveRetainedFilterState = useCallback(
    (tableKey, incomingState = {}) => {
      if (!hydrated) return;
      if (!tableKey) return;
      const retained = state?.retainedOrderFilters || {};
      const prevTableState = retained?.[tableKey] || {};
      const mergedTableState = {
        ...prevTableState,
        ...incomingState,
        paginationProp: {
          ...prevTableState?.paginationProp,
          ...incomingState?.paginationProp,
        },

        filters: {
          ...prevTableState?.filters,
          ...incomingState?.filters,
        },

        filterTags: {
          ...prevTableState?.filterTags,
          ...incomingState?.filterTags,
        },

        sort: incomingState?.sort ?? prevTableState?.sort,
      };

      const updated = {
        ...retained,
        [tableKey]: mergedTableState,
      };

      dispatch({
        type: 'SET_RETAINED_ORDER_FILTERS',
        data: updated,
      });

      try {
        const encrypted = CryptoJS.AES.encrypt(
          JSON.stringify(mergedTableState),
          getKey(),
        ).toString();

        const url = new URL(window.location.href);
        url.searchParams.set('s', encrypted);
        url.searchParams.set('t', tableKey);
        window.history.replaceState(null, '', url.toString());
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('URL sync error:', error);
      }
    },
    [dispatch, state?.retainedOrderFilters, hydrated],
  );

  const resetRetainedFilterState = useCallback(
    (tableKey) => {
      const retained = state?.retainedOrderFilters || {};

      const updated = Object.fromEntries(
        Object.entries(retained).filter(([key]) => key !== tableKey),
      );

      dispatch({
        type: 'SET_RETAINED_ORDER_FILTERS',
        data: updated,
      });

      const url = new URL(window.location.href);
      url.searchParams.delete('s');
      url.searchParams.delete('t');
      window.history.replaceState(null, '', url.toString());

      window.location.reload();
    },
    [dispatch, state?.retainedOrderFilters],
  );

  const isEmptyObject = (obj) =>
    !obj || (typeof obj === 'object' && Object.keys(obj).length === 0);

  const hasRetainedFilters = useCallback(
    (tableKey) => {
      const retained = state?.retainedOrderFilters?.[tableKey];
      if (!retained) return false;
      const {
        filters,
        sort,
        searchTerm,
        paginationProp,
        filterTags,
      } = retained;

      const onlyPaginationPresent =
        isEmptyObject(filters) &&
        isEmptyObject(sort) &&
        !searchTerm &&
        isEmptyObject(filterTags);

      if (onlyPaginationPresent) {
        const pageSize = paginationProp?.pageSize;

        if (
          (toInteger(paginationProp?.current, 10) === 1 ||
            !paginationProp?.current) &&
          (pageSize == null || pageSize === LIMIT)
        ) {
          return false;
        }
      }

      return true;
    },
    [state?.retainedOrderFilters],
  );

  return {
    retainedState: state?.retainedOrderFilters || {},
    saveRetainedFilterState,
    resetRetainedFilterState,
    hasRetainedFilters,
    hydrated,
  };
};

export default useRetainedTableState;
